import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import cogoToast from '@successtar/cogo-toast'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import { FrameOption, FrameConfig } from '@renderer/types/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import {
  processAndUploadStrip,
  processAndUploadPhotos,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'

interface UserContext {
  user?: {
    email: string
  }
}

interface BackgroundOption {
  id: string
  name: string
  gradient: string
  colors: [string, string]
}

type FilterMode = 'filter' | 'background'

// Background options including no background and gradient backgrounds
const backgroundOptions: BackgroundOption[] = [
  {
    id: 'neutral',
    name: 'No Background',
    gradient: 'transparent',
    colors: ['transparent', 'transparent']
  },
  {
    id: 'sakura',
    name: 'Sakura',
    gradient: 'linear-gradient(135deg, #ffb3d9 0%, #ffe6f2 100%)',
    colors: ['#ffb3d9', '#ffe6f2']
  },
  {
    id: 'sky',
    name: 'Sky',
    gradient: 'linear-gradient(135deg, #87CEEB 0%, #E0F6FF 100%)',
    colors: ['#87CEEB', '#E0F6FF']
  },
  {
    id: 'mint',
    name: 'Mint',
    gradient: 'linear-gradient(135deg, #98FB98 0%, #F0FFF0 100%)',
    colors: ['#98FB98', '#F0FFF0']
  },
  {
    id: 'sunset',
    name: 'Sunset',
    gradient: 'linear-gradient(135deg, #FFB347 0%, #FFEECC 100%)',
    colors: ['#FFB347', '#FFEECC']
  },
  {
    id: 'lavender',
    name: 'Lavender',
    gradient: 'linear-gradient(135deg, #E6E6FA 0%, #F8F8FF 100%)',
    colors: ['#E6E6FA', '#F8F8FF']
  }
]

// MediaPipe Image Segmentation for human detection
interface MediaPipeSegmentationResult {
  categoryMask: {
    getAsUint8Array: () => Uint8Array
    width: number
    height: number
  }
}

let imageSegmenter: unknown = null

const initializeImageSegmenter = async (): Promise<unknown> => {
  if (imageSegmenter) return imageSegmenter

  try {
    // Import MediaPipe Tasks Vision
    const { ImageSegmenter, FilesetResolver } = await import('@mediapipe/tasks-vision')

    // Initialize the WASM runtime
    const vision = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
    )

    // Create the image segmenter
    imageSegmenter = await ImageSegmenter.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath:
          'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/latest/selfie_multiclass_256x256.tflite',
        delegate: 'GPU'
      },
      runningMode: 'IMAGE',
      outputCategoryMask: true,
      outputConfidenceMasks: false
    })

    return imageSegmenter
  } catch (error) {
    console.error('Failed to initialize MediaPipe Image Segmenter:', error)
    // Fallback to simpler edge detection
    return null
  }
}

// Fallback: Simple edge detection for background removal
const simpleBackgroundRemoval = (imageData: ImageData, gradientData: ImageData): ImageData => {
  const data = imageData.data
  const width = imageData.width
  const height = imageData.height

  // Create edge map
  const edges = new Uint8Array(width * height)

  // Sobel edge detection with improved quality
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      // Get surrounding pixels with proper color weighting
      const tl =
        data[((y - 1) * width + (x - 1)) * 4] * 0.299 +
        data[((y - 1) * width + (x - 1)) * 4 + 1] * 0.587 +
        data[((y - 1) * width + (x - 1)) * 4 + 2] * 0.114
      const tm =
        data[((y - 1) * width + x) * 4] * 0.299 +
        data[((y - 1) * width + x) * 4 + 1] * 0.587 +
        data[((y - 1) * width + x) * 4 + 2] * 0.114
      const tr =
        data[((y - 1) * width + (x + 1)) * 4] * 0.299 +
        data[((y - 1) * width + (x + 1)) * 4 + 1] * 0.587 +
        data[((y - 1) * width + (x + 1)) * 4 + 2] * 0.114
      const ml =
        data[(y * width + (x - 1)) * 4] * 0.299 +
        data[(y * width + (x - 1)) * 4 + 1] * 0.587 +
        data[(y * width + (x - 1)) * 4 + 2] * 0.114
      const mr =
        data[(y * width + (x + 1)) * 4] * 0.299 +
        data[(y * width + (x + 1)) * 4 + 1] * 0.587 +
        data[(y * width + (x + 1)) * 4 + 2] * 0.114
      const bl =
        data[((y + 1) * width + (x - 1)) * 4] * 0.299 +
        data[((y + 1) * width + (x - 1)) * 4 + 1] * 0.587 +
        data[((y + 1) * width + (x - 1)) * 4 + 2] * 0.114
      const bm =
        data[((y + 1) * width + x) * 4] * 0.299 +
        data[((y + 1) * width + x) * 4 + 1] * 0.587 +
        data[((y + 1) * width + x) * 4 + 2] * 0.114
      const br =
        data[((y + 1) * width + (x + 1)) * 4] * 0.299 +
        data[((y + 1) * width + (x + 1)) * 4 + 1] * 0.587 +
        data[((y + 1) * width + (x + 1)) * 4 + 2] * 0.114

      // Sobel X and Y
      const sobelX = tr + 2 * mr + br - (tl + 2 * ml + bl)
      const sobelY = bl + 2 * bm + br - (tl + 2 * tm + tr)
      const magnitude = Math.sqrt(sobelX * sobelX + sobelY * sobelY)

      edges[y * width + x] = magnitude > 80 ? 255 : 0 // Lower threshold for better edge detection
    }
  }

  // Fill algorithm to identify background regions with improved logic
  const backgroundMask = new Uint8Array(width * height)
  const visited = new Array(width * height).fill(false)

  // Start flood fill from edges (likely background)
  const stack: Array<{ x: number; y: number }> = []

  // Add edge pixels to stack
  for (let x = 0; x < width; x++) {
    stack.push({ x, y: 0 })
    stack.push({ x, y: height - 1 })
  }
  for (let y = 0; y < height; y++) {
    stack.push({ x: 0, y })
    stack.push({ x: width - 1, y })
  }

  while (stack.length > 0) {
    const { x, y } = stack.pop()!
    const idx = y * width + x

    if (x < 0 || x >= width || y < 0 || y >= height || visited[idx]) continue

    const pixelIdx = idx * 4
    const r = data[pixelIdx]
    const g = data[pixelIdx + 1]
    const b = data[pixelIdx + 2]
    const brightness = r * 0.299 + g * 0.587 + b * 0.114 // Use proper luminance formula

    // Improved background detection with color variance check
    const colorVariance = Math.sqrt(
      Math.pow(r - brightness, 2) + Math.pow(g - brightness, 2) + Math.pow(b - brightness, 2)
    )

    // If it's a light pixel with low color variance and not an edge, mark as background
    if (brightness > 160 && colorVariance < 30 && edges[idx] < 128) {
      visited[idx] = true
      backgroundMask[idx] = 255

      // Add neighbors
      stack.push({ x: x + 1, y })
      stack.push({ x: x - 1, y })
      stack.push({ x, y: y + 1 })
      stack.push({ x, y: y - 1 })
    }
  }

  // Apply background replacement with alpha preservation
  for (let i = 0; i < data.length; i += 4) {
    const pixelIdx = Math.floor(i / 4)
    if (backgroundMask[pixelIdx] > 128) {
      // Replace background pixel with gradient while preserving alpha
      data[i] = gradientData.data[i]
      data[i + 1] = gradientData.data[i + 1]
      data[i + 2] = gradientData.data[i + 2]
      data[i + 3] = 255 // Ensure full opacity for background pixels
    }
    // Keep original alpha for non-background pixels
  }

  return imageData
}

// Enhanced background processing with human detection
const processBackgroundRemoval = async (
  imageUrl: string,
  backgroundOption: BackgroundOption
): Promise<string> => {
  // If neutral background is selected, return the original image without processing
  if (backgroundOption.id === 'neutral') {
    return imageUrl
  }

  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = async (): Promise<void> => {
      try {
        // Get device pixel ratio for high-DPI displays
        const devicePixelRatio = window.devicePixelRatio || 1

        // Create high-resolution canvas
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          reject(new Error('Could not get canvas context'))
          return
        }

        // Set canvas size accounting for device pixel ratio
        canvas.width = img.width * devicePixelRatio
        canvas.height = img.height * devicePixelRatio

        // Scale the context to match the device pixel ratio
        ctx.scale(devicePixelRatio, devicePixelRatio)

        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'

        // Draw original image at full resolution
        ctx.drawImage(img, 0, 0, img.width, img.height)

        // Create gradient background at same resolution
        const gradientCanvas = document.createElement('canvas')
        const gradientCtx = gradientCanvas.getContext('2d')

        if (!gradientCtx) {
          reject(new Error('Could not get gradient canvas context'))
          return
        }

        gradientCanvas.width = canvas.width
        gradientCanvas.height = canvas.height

        // Scale gradient context to match device pixel ratio
        gradientCtx.scale(devicePixelRatio, devicePixelRatio)

        // Create gradient
        const gradient = gradientCtx.createLinearGradient(0, 0, img.width, img.height)
        gradient.addColorStop(0, backgroundOption.colors[0])
        gradient.addColorStop(1, backgroundOption.colors[1])

        gradientCtx.fillStyle = gradient
        gradientCtx.fillRect(0, 0, img.width, img.height)

        const gradientData = gradientCtx.getImageData(0, 0, canvas.width, canvas.height)

        try {
          // Try to use MediaPipe Image Segmenter
          const segmenter = await initializeImageSegmenter()

          if (segmenter && typeof segmenter === 'object' && 'segment' in segmenter) {
            // Use MediaPipe for human detection
            const segmentationResult = (
              segmenter as { segment: (img: HTMLImageElement) => unknown }
            ).segment(img)

            if (
              segmentationResult &&
              typeof segmentationResult === 'object' &&
              'categoryMask' in segmentationResult
            ) {
              // Apply segmentation mask
              const mask = (segmentationResult as MediaPipeSegmentationResult).categoryMask
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
              const data = imageData.data

              // Get mask data - MediaPipe returns Uint8Array
              const maskData = mask.getAsUint8Array()
              const maskWidth = mask.width
              const maskHeight = mask.height

              // Apply background replacement based on segmentation
              for (let y = 0; y < canvas.height; y++) {
                for (let x = 0; x < canvas.width; x++) {
                  const canvasIdx = (y * canvas.width + x) * 4

                  // Scale coordinates to mask dimensions
                  const maskX = Math.floor((x / canvas.width) * maskWidth)
                  const maskY = Math.floor((y / canvas.height) * maskHeight)
                  const maskIdx = maskY * maskWidth + maskX

                  // Category 0 = background, 1 = person (hair), 2 = person (body-skin), 3 = person (face-skin), 4 = person (clothes)
                  const category = maskData[maskIdx]

                  if (category === 0) {
                    // Background pixel
                    data[canvasIdx] = gradientData.data[canvasIdx]
                    data[canvasIdx + 1] = gradientData.data[canvasIdx + 1]
                    data[canvasIdx + 2] = gradientData.data[canvasIdx + 2]
                    // Preserve original alpha channel
                    data[canvasIdx + 3] = 255
                  }
                }
              }

              ctx.putImageData(imageData, 0, 0)
            } else {
              // Fallback to simple method
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
              const processedData = simpleBackgroundRemoval(imageData, gradientData)
              ctx.putImageData(processedData, 0, 0)
            }
          } else {
            // Fallback to simple edge detection method
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
            const processedData = simpleBackgroundRemoval(imageData, gradientData)
            ctx.putImageData(processedData, 0, 0)
          }
        } catch (mpError) {
          console.log('MediaPipe failed, using fallback method:', mpError)
          // Fallback to simple edge detection method
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const processedData = simpleBackgroundRemoval(imageData, gradientData)
          ctx.putImageData(processedData, 0, 0)
        }

        // Convert to blob with PNG format for lossless quality
        canvas.toBlob((blob) => {
          if (blob) {
            const processedUrl = URL.createObjectURL(blob)
            resolve(processedUrl)
          } else {
            reject(new Error('Failed to create blob'))
          }
        }, 'image/png') // Use PNG for lossless quality instead of JPEG
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = (): void => {
      reject(new Error('Failed to load image'))
    }

    img.src = imageUrl
  })
}

const Filter: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const {
    gallery,
    galleryKey,
    userPreferences,
    setSessionState,
    logActivity,
    id
  } = useSessionStateStore()
  const { filters, getFrame, frames } = useConfigStore()
  const { downloadLocal } = useSettingsStore()

  // Check if we're in photostrip mode
  const isPhotostripMode = userPreferences.boothMode === 'photostrip'

  const [filterMode, setFilterMode] = useState<FilterMode>('filter')
  const [selectedFilterId, setSelectedFilterId] = useState<string>(
    userPreferences.filterId || filters[0]._id
  )
  const [selectedBackgroundId, setSelectedBackgroundId] = useState<string>(
    isPhotostripMode ? 'neutral' : backgroundOptions[0].id
  )
  const [processedBackgroundUrl, setProcessedBackgroundUrl] = useState<string | null>(null)
  const [isProcessingLocked, setIsProcessingLocked] = useState(false)
  const [isBackgroundProcessing, setIsBackgroundProcessing] = useState(false)
  const [backgroundProcessingProgress, setBackgroundProcessingProgress] = useState<number>(0)
  const [backgroundProcessingStatus, setBackgroundProcessingStatus] = useState<string>('')
  // Track photostrip processing state to show UI and prevent multiple clicks
  const [isPhotostripProcessing, setIsPhotostripProcessing] = useState(false)
  const previewRef = useRef<PrintPreviewRef>(null)

  // Get frame configuration
  const getFrameConfig = (frame: FrameOption | null): FrameConfig => {
    if (!frame)
      return {
        id: '',
        baseImage: '',
        drawParams: {
          canvasWidth: 1200,
          canvasHeight: 1800,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }
    return {
      id: frame._id,
      baseImage: frame.thumbnail,
      drawParams: frame.drawParams
    }
  }

  const selectedFrame = getFrame(userPreferences.frameId || frames[0]._id) || null
  const frameConfig = getFrameConfig(selectedFrame)

  // Get captured photos for preview
  const capturedPhotos = gallery.capturedItems
    .filter((item) => item.photoUrl)
    .map((item) => item.photoUrl)

  // Get first photo for filter preview
  const firstPhoto = capturedPhotos[0] || ''

  // Get filtered photos from cache or fallback to original
  const getPhotosForFilter = (filterId: string): string[] => {
    const filterCache = gallery.filterCache[filterId]
    if (filterCache?.photos && filterCache.photos.length === capturedPhotos.length) {
      return filterCache.photos
    }

    // Fallback: try to get individual filtered versions
    const fallbackPhotos = gallery.capturedItems
      .filter((item) => item.photoUrl)
      .map((item) => item.filteredVersions?.[filterId] || item.photoUrl)

    return fallbackPhotos
  }

  // For filter preview: get the first filtered photo (if available)
  const getFirstFilteredPhoto = (filterId: string): string | undefined => {
    const firstItem = gallery.capturedItems.find((item) => item.photoUrl)
    return firstItem?.filteredVersions?.[filterId] || firstItem?.photoUrl
  }

  // Check if filter is ready (all photos processed)
  const isFilterReady = (filterId: string): boolean => {
    const filterCache = gallery.filterCache[filterId]
    if (filterCache?.isComplete) return true

    // Check if all photos have this filter applied individually
    const processedCount = gallery.capturedItems.filter(
      (item) => item.photoUrl && item.filteredVersions?.[filterId]
    ).length

    return processedCount === capturedPhotos.length
  }

  // Update session state when filter changes
  useEffect(() => {
    setSessionState({
      userPreferences: {
        filterId: selectedFilterId
      }
    })
  }, [selectedFilterId])

  // Reset processing lock when component unmounts
  useEffect(() => {
    return (): void => {
      setIsProcessingLocked(false)
    }
  }, [])

  // Set processed background URL for photostrip mode (no background processing needed)
  useEffect(() => {
    if (isPhotostripMode && firstPhoto) {
      setProcessedBackgroundUrl(firstPhoto)
    }
  }, [isPhotostripMode, firstPhoto])

  const handleFilterSelect = (filterId: string): void => {
    setSelectedFilterId(filterId)
    logActivity(`Filter selected: ${filters.find((f) => f._id === filterId)?.name || filterId}`)
  }

  const handleFilterNext = (): void => {
    // If in photostrip mode, skip background selection and go directly to processing
    if (isPhotostripMode) {
      // Set processing states to prevent multiple clicks and show UI
      // This ensures the same processing experience as Japanese mode
      setIsProcessingLocked(true)
      setIsPhotostripProcessing(true)
      handleBackgroundDone()
      return
    }

    logActivity('Proceeding to background selection')
    setFilterMode('background')
  }

  const handleBackgroundSelect = useCallback(
    async (backgroundId: string) => {
      setSelectedBackgroundId(backgroundId)

      const backgroundOption = backgroundOptions.find((bg) => bg.id === backgroundId)
      if (!backgroundOption || !firstPhoto) {
        return
      }

      // If neutral background is selected or in photostrip mode, no processing is needed
      if (backgroundOption.id === 'neutral' || isPhotostripMode) {
        logActivity(`Background selected: ${backgroundOption.name} (no processing)`)
        setProcessedBackgroundUrl(firstPhoto) // Use original photo
        return
      }

      // For other backgrounds, show processing states
      setIsBackgroundProcessing(true)
      setBackgroundProcessingProgress(0)
      setBackgroundProcessingStatus('Initializing background processing...')

      try {
        logActivity(`Background selected: ${backgroundOption.name}`)

        // Get the filtered photo to apply background to
        const filteredPhoto = getFirstFilteredPhoto(selectedFilterId) || firstPhoto

        setBackgroundProcessingProgress(25)
        setBackgroundProcessingStatus('Loading image data...')

        // Process the filtered photo with the selected background
        const processedUrl = await processBackgroundRemoval(filteredPhoto, backgroundOption)

        setBackgroundProcessingProgress(100)
        setBackgroundProcessingStatus('Background applied successfully!')
        setProcessedBackgroundUrl(processedUrl)

        // Clear status after a short delay
        setTimeout(() => {
          setBackgroundProcessingStatus('')
        }, 2000)
      } catch (error) {
        console.error('Error processing background:', error)
        setBackgroundProcessingStatus('Failed to process background')
        cogoToast.error('Failed to process background')

        // Clear error status after a delay
        setTimeout(() => {
          setBackgroundProcessingStatus('')
        }, 3000)
      } finally {
        setIsBackgroundProcessing(false)
        setBackgroundProcessingProgress(0)
      }
    },
    [firstPhoto, selectedFilterId, setSessionState, logActivity, isPhotostripMode]
  )

  const handleBackgroundDone = async (): Promise<void> => {
    logActivity('Proceeding to processing and printing')

    // Set processing states
    setIsProcessingLocked(true)

    try {
      // Get the filtered photos
      const filteredPhotos = getPhotosForFilter(selectedFilterId)

      // Process all filtered photos with the selected background
      const processedPhotos: string[] = []
      const backgroundOption = backgroundOptions.find((bg) => bg.id === selectedBackgroundId)

      if (backgroundOption) {
        // For neutral background or photostrip mode, no processing is needed - use original photos
        if (backgroundOption.id === 'neutral' || isPhotostripMode) {
          processedPhotos.push(...filteredPhotos)
        } else {
          // Process photos with other backgrounds
          for (const photo of filteredPhotos) {
            try {
              const processedUrl = await processBackgroundRemoval(photo, backgroundOption)
              processedPhotos.push(processedUrl)
            } catch (error) {
              console.error('Error processing photo:', error)
              // Fallback to original photo
              processedPhotos.push(photo)
            }
          }
        }
      } else {
        processedPhotos.push(...filteredPhotos)
      }

      // Update the processed photos state for PrintPreview
      setProcessedPhotos(processedPhotos)

      const ctx: ProcessingContext = {
        context,
        projectId,
        sessionId: id || '',
        galleryId: galleryKey || gallery.id,
        downloadLocal,
        isOnline: true
      }

      const photoItems = processedPhotos.map((photoUrl) => ({ photoUrl }))
      await processAndUploadPhotos(photoItems, ctx)
      const gAfterPhotos = useSessionStateStore.getState().gallery
      setSessionState({
        gallery: {
          ...gAfterPhotos,
          individualPhotosUploaded: true,
          individualPhotosUploadedAt: Date.now()
        }
      })

      await new Promise((resolve) => setTimeout(resolve, 500))

      const stripBlob = await previewRef.current?.exportImage()
      if (!stripBlob) {
        throw new Error('Failed to export strip image')
      }

      const stripResult = await processAndUploadStrip(stripBlob, ctx)
      const stripLocalUrl = stripResult?.localUrl ?? URL.createObjectURL(stripBlob)

      setSessionState({
        gallery: {
          stripUrl: stripLocalUrl
        }
      })

      stateUploader.uploadSessionState()
      // Navigate to select page for sticker editing
      logActivity('Background processing completed, proceeding to sticker editing')
      navigate(`/${projectId}/select`)
    } catch (error) {
      console.error('Error in handleBackgroundDone:', error)
      stateUploader.uploadSessionState()
      navigate(`/${projectId}/select`)
    } finally {
      setIsProcessingLocked(false)
      setIsPhotostripProcessing(false)
    }
  }

  // Get the photos to display based on current filter
  const photosToDisplay = getPhotosForFilter(selectedFilterId)

  // State to store processed photos with backgrounds
  const [processedPhotos, setProcessedPhotos] = useState<string[]>([])

  // Call all hooks at the top level
  const cachedFilterBg = useCachedBackgroundImage('/assets/boothndaughter/filter.webp')
  const cachedCancelBg = useCachedBackgroundImage('/assets/boothndaughter/cancel.webp')
  const cachedDoneBg = useCachedBackgroundImage('/assets/boothndaughter/done.webp')
  const cachedNextBg = useCachedBackgroundImage('/assets/boothndaughter/next.webp')

  if (filterMode === 'filter') {
    return (
      <div
        className="w-full h-full relative overflow-hidden"
        style={{
          backgroundImage: `url(${cachedFilterBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Next/Done Button - Top Right */}
        <div className="absolute top-8 right-8">
          <button
            onClick={handleFilterNext}
            disabled={!selectedFilterId || isPhotostripProcessing}
            title={isPhotostripMode ? 'Done (Skip Background)' : 'Next'}
            style={{
              width: '179px',
              height: '107px',
              backgroundImage: isPhotostripMode ? `url(${cachedDoneBg})` : `url(${cachedNextBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              border: 'none',
              cursor: selectedFilterId && !isPhotostripProcessing ? 'pointer' : 'not-allowed',
              opacity: selectedFilterId && !isPhotostripProcessing ? 1 : 0.5
            }}
            className="hover:scale-105 transition-transform disabled:hover:scale-100"
          />
        </div>

        {/* Single Photo Preview - Top Center */}
        {firstPhoto && (
          <div className="absolute top-36 left-1/2 transform -translate-x-1/2">
            <div
              className="relative"
              style={{
                width: '400px',
                height: '400px'
              }}
            >
              <img
                src={getFirstFilteredPhoto(selectedFilterId) || firstPhoto}
                alt="Preview"
                className="w-full h-full object-cover rounded-lg shadow-xl"
                style={{
                  border: '4px solid white'
                }}
              />
            </div>
          </div>
        )}

        {/* Filter Options Grid */}
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 w-full max-w-4xl px-8">
          <div className="grid grid-cols-4 gap-6">
            {filters.map((filter, index) => {
              // Alternate between filter-option-1.webp and filter-option-2.webp
              const backgroundImage =
                index % 2 === 0
                  ? 'url(/assets/boothndaughter/filter-option-1.webp)'
                  : 'url(/assets/boothndaughter/filter-option-2.webp)'

              const filterReady = isFilterReady(filter._id)

              return (
                <button
                  key={filter._id}
                  onClick={() => handleFilterSelect(filter._id)}
                  disabled={isProcessingLocked || isPhotostripProcessing}
                  className="relative cursor-pointer transition-all hover:scale-105 disabled:pointer-events-none"
                  style={{
                    transform: selectedFilterId === filter._id ? 'scale(1.05)' : 'scale(1)',
                    filter: selectedFilterId === filter._id ? 'brightness(1.2)' : 'brightness(1)'
                  }}
                >
                  <div
                    className="w-full aspect-square flex items-center justify-center relative"
                    style={{
                      backgroundImage: backgroundImage,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }}
                  >
                    {/* Filter Preview */}
                    {firstPhoto && (
                      <div className="w-3/4 h-3/4 rounded-lg overflow-hidden">
                        <img
                          src={getFirstFilteredPhoto(filter._id)}
                          alt={filter.name}
                          className="w-full h-full object-cover"
                          crossOrigin="anonymous"
                        />
                      </div>
                    )}

                    {/* Selection indicator */}
                    {selectedFilterId === filter._id && (
                      <div className="absolute inset-0 border-4 border-blue-500 rounded-lg" />
                    )}

                    {/* Processing Indicator */}
                    {!filterReady && (
                      <div className="absolute top-2 right-2">
                        <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse shadow-md"></div>
                      </div>
                    )}
                  </div>

                  {/* Filter Name */}
                  <div className="mt-2 text-center">
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '18px',
                        color: 'white',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                      }}
                    >
                      {filter.name}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundImage: `url(${cachedFilterBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Processing Overlay */}
      {(isProcessingLocked || isPhotostripProcessing) && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white/90 px-8 py-6 rounded-2xl shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing...</h2>
              <p className="text-gray-600">Please wait while we process your photos</p>
            </div>
          </div>
        </div>
      )}

      {/* Global Processing Status Indicator */}
      {isBackgroundProcessing && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span className="font-bold">Processing Background...</span>
            {backgroundProcessingStatus && (
              <span className="text-sm opacity-90">({backgroundProcessingStatus})</span>
            )}
          </div>
        </div>
      )}

      {/* Back to Filter Button - Top Left */}
      <div className="absolute top-8 left-8">
        <button
          onClick={() => setFilterMode('filter')}
          disabled={isProcessingLocked}
          title="Back to Filter"
          style={{
            width: '179px',
            height: '107px',
            backgroundImage: `url(${cachedCancelBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            border: 'none',
            cursor: !isProcessingLocked ? 'pointer' : 'not-allowed',
            opacity: !isProcessingLocked ? 1 : 0.5
          }}
          className="hover:scale-105 transition-transform disabled:hover:scale-100"
        />
      </div>

      {/* Done Button - Top Right */}
      <div className="absolute top-8 right-8">
        <button
          onClick={handleBackgroundDone}
          disabled={isBackgroundProcessing}
          title="Done"
          style={{
            width: '179px',
            height: '107px',
            backgroundImage: `url(${cachedDoneBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            border: 'none',
            cursor: !isBackgroundProcessing ? 'pointer' : 'not-allowed',
            opacity: !isBackgroundProcessing ? 1 : 0.5
          }}
          className="hover:scale-105 transition-transform disabled:hover:scale-100"
        />
      </div>

      {/* Single Photo Preview - Top Center */}
      {firstPhoto && (
        <div className="absolute top-36 left-1/2 transform -translate-x-1/2">
          <div
            className="relative"
            style={{
              width: '400px',
              height: '400px'
            }}
          >
            <img
              src={processedBackgroundUrl || getFirstFilteredPhoto(selectedFilterId) || firstPhoto}
              alt="Preview"
              className="w-full h-full object-cover rounded-lg shadow-xl"
              style={{
                border: '4px solid white'
              }}
            />

            {/* Background Processing Overlay */}
            {isBackgroundProcessing && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <div className="text-white text-lg font-bold mb-2">Processing Background...</div>
                  {backgroundProcessingProgress > 0 && (
                    <div className="w-48 bg-gray-700 rounded-full h-2 mb-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${backgroundProcessingProgress}%` }}
                      ></div>
                    </div>
                  )}
                  {backgroundProcessingStatus && (
                    <div className="text-white text-sm">{backgroundProcessingStatus}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Background Options Grid */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 w-full max-w-4xl px-8">
        <div className="grid grid-cols-6 gap-4">
          {backgroundOptions.map((background, index) => {
            // Alternate between filter-option-1.webp and filter-option-2.webp
            const backgroundImage =
              index % 2 === 0
                ? 'url(/assets/boothndaughter/filter-option-1.webp)'
                : 'url(/assets/boothndaughter/filter-option-2.webp)'

            return (
              <button
                key={background.id}
                onClick={() => handleBackgroundSelect(background.id)}
                disabled={isBackgroundProcessing}
                className="relative cursor-pointer transition-all hover:scale-105 disabled:pointer-events-none"
                style={{
                  transform: selectedBackgroundId === background.id ? 'scale(1.05)' : 'scale(1)',
                  filter:
                    selectedBackgroundId === background.id ? 'brightness(1.2)' : 'brightness(1)'
                }}
              >
                <div
                  className="w-full aspect-square flex items-center justify-center relative"
                  style={{
                    backgroundImage: backgroundImage,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  {/* Background Preview */}
                  <div
                    className="w-3/4 h-3/4 rounded-lg overflow-hidden relative"
                    style={{
                      background:
                        background.id === 'neutral'
                          ? 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)'
                          : background.gradient,
                      backgroundSize: background.id === 'neutral' ? '20px 20px' : 'cover',
                      backgroundPosition:
                        background.id === 'neutral'
                          ? '0 0, 0 10px, 10px -10px, -10px 0px'
                          : 'center'
                    }}
                  >
                    {/* Show "No Background" text for neutral option */}
                    {background.id === 'neutral' && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs text-gray-500 font-bold bg-white/80 px-2 py-1 rounded">
                          No Background
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Selection indicator */}
                  {selectedBackgroundId === background.id && (
                    <div className="absolute inset-0 border-4 border-blue-500 rounded-lg" />
                  )}

                  {/* Processing Indicator */}
                  {isBackgroundProcessing && selectedBackgroundId === background.id && (
                    <div className="absolute top-2 right-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse shadow-md"></div>
                    </div>
                  )}

                  {/* Processing Status Overlay */}
                  {isBackgroundProcessing && selectedBackgroundId === background.id && (
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center rounded-lg">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <div className="text-white text-xs font-bold">Processing...</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Background Name */}
                <div className="mt-2 text-center">
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '16px',
                      color: 'white',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                    }}
                  >
                    {background.name}
                  </span>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Hidden PrintPreview for processing */}
      <div className="hidden">
        <PrintPreview
          ref={previewRef}
          frameConfig={frameConfig}
          selectedPhotos={processedPhotos.length > 0 ? processedPhotos : photosToDisplay}
          isInteractive={false}
          sessionId={galleryKey || gallery.id}
        />
      </div>
    </div>
  )
}

export default Filter
